// Native-only: watches for a WorkManager-driven upload job (CmUploadWorker)
// matching this event, purely for display - it never resumes or re-runs
// anything (native never stopped running it; this just reads its state back).
// Replaces the old per-page `resumeJob` reconciliation `useEffect`s, which
// called `listIncompleteJobs()` then re-*ran* the job via `runUploadJob` -
// that re-run is exactly what the WorkManager rewrite eliminates: the job is
// always already running (or already finished) on native, so JS only ever
// needs to look.
import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { nativeUploadService } from './nativeUploadService';

const POLL_MS = 2500;

export function useNativeUploadObserver(eventId, flowKinds) {
  const [job, setJob] = useState(null);
  const flowKindsRef = useRef(flowKinds);
  flowKindsRef.current = flowKinds;

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !eventId) return undefined;
    let cancelled = false;
    let timerId = null;

    const findMine = (jobs) => (jobs || []).find((j) => String(j.eventId) === String(eventId) && flowKindsRef.current.includes(j.flowKind));

    const tick = async () => {
      const { jobs } = await nativeUploadService.listActiveJobs();
      if (cancelled) return;
      const mine = findMine(jobs);
      setJob(mine || null);
      // Always poll at the same short interval, not just while something is
      // already known to be active - listActiveJobs() is a local read (no
      // network), so there's no real cost to it. A back-off-when-idle
      // cadence here previously meant a short-lived job (a small auto-sync
      // batch in particular - compress+upload of a handful of photos can
      // easily finish in a few seconds) could start and finish entirely
      // between two slow-cadence ticks, so `job` never flipped truthy for
      // its whole lifetime and the in-app progress UI (NativeUploadProgress)
      // never rendered - confirmed as the reason auto-sync's compress/upload
      // progress wasn't showing on UploadLinkPage even though the same
      // native job/notification pipeline worked fine for manual uploads,
      // whose larger/slower batches were far more likely to straddle a tick.
      timerId = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { cancelled = true; if (timerId) clearTimeout(timerId); };
  }, [eventId]);

  return job;
}
